import React, { useState, useRef } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  Grid,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import PaletteIcon from '@mui/icons-material/Palette';
import UploadIcon from '@mui/icons-material/Upload';
import LogoUploader from './LogoUploader';
import ColorControls from './ColorControls';
import LogoPreview from './LogoPreview';
import ColorPresets from './ColorPresets';

function Changer() {
  const [logo, setLogo] = useState(null);
  const [currentColor, setCurrentColor] = useState('#1976d2');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [originalImageData, setOriginalImageData] = useState(null);
  const [error, setError] = useState('');
  const canvasRef = useRef(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleLogoUpload = (file, imageUrl, imageData) => {
    setLogo(imageUrl);
    setOriginalImageData(imageData);
    setError('');
  };

  const handleColorChange = (color) => {
    setCurrentColor(color.hex);
  };

  const handleBackgroundColorChange = (color) => {
    setBackgroundColor(color.hex);
  };

  const resetLogo = () => {
    setCurrentColor('#1976d2');
    setBackgroundColor('#ffffff');
    setError('');
  };

  const downloadLogo = () => {
    if (canvasRef.current) {
      try {
        const canvas = canvasRef.current;
        const link = document.createElement('a');
        link.download = `logo-${currentColor.replace('#', '')}-${Date.now()}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      } catch (err) {
        setError('Failed to download logo');
      }
    }
  };

  const handleUploadError = (errorMessage) => {
    setError(errorMessage);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <br/><br/><br/>
      {/* Header */}
      <Box textAlign="center" mb={6}>
        <Box display="flex" alignItems="center" justifyContent="center" gap={2} mb={2}>
          <PaletteIcon sx={{ fontSize: 48, color: 'primary.main' }} />
          <Typography variant="h3" component="h1" fontWeight="bold" color="primary">
            Logo Color Changer
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary">
          Upload any logo and change its colors instantly
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Panel - Controls */}
        <Grid item xs={12} md={5} lg={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Logo Uploader */}
            <Paper elevation={3} sx={{ p: 3 }}>
              <LogoUploader 
                onLogoUpload={handleLogoUpload}
                onError={handleUploadError}
                currentColor={currentColor}
              />
            </Paper>

            {/* Color Controls & Presets */}
            {logo && (
              <>
                <Paper elevation={3} sx={{ p: 3 }}>
                  <ColorControls
                    currentColor={currentColor}
                    backgroundColor={backgroundColor}
                    onColorChange={handleColorChange}
                    onBackgroundColorChange={handleBackgroundColorChange}
                    onReset={resetLogo}
                    onDownload={downloadLogo}
                  />
                </Paper>

                <Paper elevation={3} sx={{ p: 3 }}>
                  <ColorPresets onColorSelect={handleColorChange} />
                </Paper>
              </>
            )}
          </Box>
        </Grid>

        {/* Right Panel - Preview */}
        <Grid item xs={12} md={7} lg={8}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 3, 
              height: '100%',
              minHeight: isMobile ? '400px' : '600px',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {logo ? (
              <LogoPreview
                logo={logo}
                color={currentColor}
                backgroundColor={backgroundColor}
                canvasRef={canvasRef}
                originalImageData={originalImageData}
              />
            ) : (
              <Box 
                sx={{ 
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  color: 'text.secondary'
                }}
              >
                <UploadIcon sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
                <Typography variant="h5" gutterBottom color="grey.600">
                  Upload a Logo to Get Started
                </Typography>
                <Typography variant="body1" color="grey.500">
                  Supported formats: PNG, JPG, SVG, GIF
                </Typography>
                <Box mt={4} textAlign="left">
                  <Typography variant="subtitle2" gutterBottom color="grey.600">
                    Features:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, color: 'grey.600' }}>
                    <Typography component="li" variant="body2">
                      Instant color changes
                    </Typography>
                    <Typography component="li" variant="body2">
                      Custom background colors
                    </Typography>
                    <Typography component="li" variant="body2">
                      Color presets for quick selection
                    </Typography>
                    <Typography component="li" variant="body2">
                      High-quality downloads
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default Changer;